export const DRAFT_BOARD_STORAGE_KEY = 'bruins09:draft-night:2026:v1';
export const KEEPER_DECISIONS_STORAGE_KEY = 'bruins09:keepers:2026:v1';

const POSITION_ORDER = ['QB', 'RB', 'WR', 'TE', 'DST', 'K'];
const STARTER_MINIMUMS = { QB: 2, RB: 2, WR: 3, TE: 1 };

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function round(value, places = 1) {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}

export function flattenMarketPlayers(snapshot) {
  if (!snapshot) return [];
  const source = Array.isArray(snapshot.rankings)
    ? snapshot.rankings
    : (snapshot.managers || []).flatMap((manager) => manager.players || []);
  const byName = new Map();
  source.forEach((player) => {
    if (!player?.player || !Number.isInteger(player.overallRank)) return;
    const existing = byName.get(player.player);
    if (!existing || player.overallRank < existing.overallRank) {
      byName.set(player.player, {
        player: player.player,
        overallRank: player.overallRank,
        position: player.position || 'FLEX',
        positionRank: player.positionRank || null,
        nflTeam: player.nflTeam || 'FA',
        byeWeek: player.byeWeek || null,
        auctionValue: player.auctionValue || 0,
      });
    }
  });
  return [...byName.values()].sort((first, second) => first.overallRank - second.overallRank);
}

export function sanitizeDraftSelections(value, validOverallPicks = []) {
  const allowed = new Set(validOverallPicks.map(String));
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value).filter(([overall, player]) => (
    allowed.has(String(overall))
      && player
      && typeof player.player === 'string'
      && Number.isInteger(player.overallRank)
  )));
}

export function applyKeeperPlacements(order = [], decisions = {}) {
  const used = new Set();
  const placements = {};
  const unresolved = [];

  Object.entries(decisions).forEach(([manager, player]) => {
    if (!player?.player) return;
    if (!player.keeperCostRound) {
      placements[`no-pick:${manager}`] = { manager, ...player, noPick: true };
      return;
    }

    const slot = order.find((pick) => (
      pick.manager === manager
        && pick.round === player.keeperCostRound
        && !used.has(pick.overall)
    ));

    if (!slot) {
      unresolved.push({ manager, ...player });
      return;
    }

    used.add(slot.overall);
    placements[String(slot.overall)] = { manager, ...player, noPick: false };
  });

  return { placements, unresolved };
}

function letterGrade(score) {
  if (score >= 93) return 'A';
  if (score >= 87) return 'A−';
  if (score >= 83) return 'B+';
  if (score >= 77) return 'B';
  if (score >= 73) return 'B−';
  if (score >= 67) return 'C+';
  if (score >= 60) return 'C';
  return 'D';
}

function buildStarterCore(players) {
  const available = players.slice().sort((first, second) => (first.overallRank || 999) - (second.overallRank || 999));
  const used = new Set();
  const starters = [];
  Object.entries(STARTER_MINIMUMS).forEach(([position, count]) => {
    available.filter((player) => player.position === position).slice(0, count).forEach((player) => {
      starters.push(player);
      used.add(player.player);
    });
  });
  const flex = available.find((player) => !used.has(player.player) && ['RB', 'WR', 'TE'].includes(player.position));
  if (flex) starters.push(flex);
  return starters;
}

export function buildDraftReportCards(order = [], selections = {}, keeperDecisions = {}) {
  const managers = [...new Set(order.map((pick) => pick.manager))];
  return managers.map((manager) => {
    const managerPicks = order.filter((pick) => pick.manager === manager);
    const drafted = managerPicks
      .map((pick) => {
        const player = selections[String(pick.overall)];
        return player ? { ...player, pickOverall: pick.overall, round: pick.round } : null;
      })
      .filter(Boolean);
    const keeper = keeperDecisions[manager]?.player ? keeperDecisions[manager] : null;
    const occupiedKeeperSlots = keeper?.keeperCostRound ? 1 : 0;
    const values = drafted.map((player) => player.pickOverall - player.overallRank);
    const averageValue = values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
    const positions = Object.fromEntries(POSITION_ORDER.map((position) => [position, 0]));
    drafted.forEach((player) => {
      positions[player.position] = (positions[player.position] || 0) + 1;
    });
    if (keeper?.position) positions[keeper.position] = (positions[keeper.position] || 0) + 1;
    const rosterPlayers = [...drafted, ...(keeper ? [keeper] : [])];
    const rosterWarnings = Object.entries(STARTER_MINIMUMS)
      .filter(([position, minimum]) => (positions[position] || 0) < minimum)
      .map(([position, minimum]) => `Need ${minimum - (positions[position] || 0)} ${position}`);
    const byeCounts = rosterPlayers.reduce((counts, player) => {
      if (player.byeWeek) counts[player.byeWeek] = (counts[player.byeWeek] || 0) + 1;
      return counts;
    }, {});
    Object.entries(byeCounts).forEach(([bye, count]) => {
      if (count >= 3) rosterWarnings.push(`${count} players on bye ${bye}`);
    });
    const bestSteal = drafted.slice().sort((first, second) => (
      (second.pickOverall - second.overallRank) - (first.pickOverall - first.overallRank)
    ))[0] || null;
    const biggestReach = drafted.slice().sort((first, second) => (
      (first.pickOverall - first.overallRank) - (second.pickOverall - second.overallRank)
    ))[0] || null;
    const keeperMarketRound = keeper?.marketRank ? Math.ceil(keeper.marketRank / 10) : null;
    const keeperBonus = keeper
      ? keeper.keeperCostRound && keeperMarketRound
        ? clamp((keeper.keeperCostRound - keeperMarketRound) * 0.8, -6, 8)
        : keeper.marketRank ? 6 : 0
      : 0;
    const score = drafted.length ? clamp(75 + (averageValue * 0.65) + keeperBonus, 45, 98) : null;
    const positionAverages = Object.fromEntries(POSITION_ORDER.map((position) => {
      const ranked = rosterPlayers.filter((player) => player.position === position && player.overallRank);
      return [position, ranked.length ? ranked.reduce((sum, player) => sum + player.overallRank, 0) / ranked.length : null];
    }));
    const filledPositions = Object.entries(positionAverages).filter(([, value]) => value !== null);
    const strength = filledPositions.slice().sort((first, second) => first[1] - second[1])[0]?.[0] || null;

    return {
      manager,
      teamName: managerPicks[0]?.teamName || manager,
      drafted,
      keeper,
      pickCount: managerPicks.length,
      completedPicks: drafted.length + occupiedKeeperSlots,
      completion: managerPicks.length ? round(((drafted.length + occupiedKeeperSlots) / managerPicks.length) * 100) : 0,
      averageValue: round(averageValue),
      positions,
      rosterWarnings,
      strength,
      starterCore: buildStarterCore(rosterPlayers),
      bestSteal,
      biggestReach,
      marketScore: score === null ? null : round(score),
      marketGrade: score === null ? '—' : letterGrade(score),
    };
  }).sort((first, second) => (
    (second.marketScore ?? -1) - (first.marketScore ?? -1)
      || second.completedPicks - first.completedPicks
      || first.manager.localeCompare(second.manager)
  ));
}

export function draftBoardSummary(order = [], selections = {}, placements = {}) {
  const keeperSlots = new Set(Object.keys(placements).filter((key) => /^\d+$/.test(key)));
  const completed = order.filter((pick) => selections[String(pick.overall)] || keeperSlots.has(String(pick.overall))).length;
  return {
    totalPicks: order.length,
    completed,
    remaining: Math.max(0, order.length - completed),
    percent: order.length ? round((completed / order.length) * 100) : 0,
  };
}
